import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Cliente activo de la interfaz (decisión D1 del rediseño UX, 16/09/2026):
 * un solo menú y un conmutador GAME / TICNOVA arriba. Las rutas siguen
 * siendo /game/… y /ticnova/…; el conmutador cambia el prefijo y, si la
 * página actual existe para el otro cliente, salta a la equivalente.
 *
 * Fuente de verdad: la URL cuando empieza por /game o /ticnova; si no
 * (Inicio, Proveedores, Administración), el último elegido (localStorage).
 */

export type ClienteCodigo = 'GAME' | 'TICNOVA';
export const CLIENTES: { codigo: ClienteCodigo; nombre: string; base: string }[] = [
  { codigo: 'GAME', nombre: 'GAME', base: '/game' },
  { codigo: 'TICNOVA', nombre: 'TICNOVA', base: '/ticnova' },
];
const KEY = 'pm-approx:cliente-activo';

type Ctx = { cliente: ClienteCodigo; base: string; setCliente: (c: ClienteCodigo) => void };
const ClienteActivoContext = createContext<Ctx>({ cliente: 'GAME', base: '/game', setCliente: () => {} });

function desdeRuta(pathname: string): ClienteCodigo | null {
  const m = pathname.match(/^\/(game|ticnova)(\/|$)/);
  return m ? (m[1].toUpperCase() as ClienteCodigo) : null;
}

function leerGuardado(): ClienteCodigo {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'GAME' || v === 'TICNOVA') return v;
  } catch { /* sin storage */ }
  return 'GAME';
}

export function ClienteActivoProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [guardado, setGuardado] = useState<ClienteCodigo>(leerGuardado);
  const enRuta = desdeRuta(location.pathname);
  const cliente = enRuta ?? guardado;

  // La URL manda: al navegar a /ticnova/… el conmutador se pone en TICNOVA.
  useEffect(() => {
    if (enRuta && enRuta !== guardado) {
      setGuardado(enRuta);
      try { localStorage.setItem(KEY, enRuta); } catch { /* sin storage */ }
    }
  }, [enRuta, guardado]);

  const setCliente = useCallback((c: ClienteCodigo) => {
    setGuardado(c);
    try { localStorage.setItem(KEY, c); } catch { /* sin storage */ }
    const actual = desdeRuta(location.pathname);
    if (actual && actual !== c) {
      // Misma página en el otro cliente, sin el id de detalle (que es de un
      // registro del cliente anterior): /game/productos/123 → /ticnova/productos.
      const resto = location.pathname.replace(/^\/(game|ticnova)/, '');
      const primerSegmento = resto.split('/').filter(Boolean)[0];
      const destino = `${c === 'GAME' ? '/game' : '/ticnova'}${primerSegmento ? `/${primerSegmento}` : ''}`;
      navigate(destino);
    }
  }, [location.pathname, navigate]);

  const value = useMemo<Ctx>(() => ({ cliente, base: cliente === 'GAME' ? '/game' : '/ticnova', setCliente }), [cliente, setCliente]);
  return <ClienteActivoContext.Provider value={value}>{children}</ClienteActivoContext.Provider>;
}

export function useClienteActivo(): Ctx {
  return useContext(ClienteActivoContext);
}
